import { useState } from 'react';
import { useSelector } from 'react-redux';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { PilotId } from '~/eds/src/types/PilotId';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { EsignatureRecipient } from '~/features/ticket/types/esignature';
import { api } from '~/redux/api/createApi';
import { selectTicketId, selectTicketPermissions } from '~/redux/selectors';

import { useReassignSignerModal } from './useReassignSignerModal';

/**
 * Handles all logic related to the assignment of a signer on the ticket page
 */
export const useTicketRecipientAssignment = (
  recipient: EsignatureRecipient,
) => {
  const { toast } = useErrorToast();
  const ticketId = useSelector(selectTicketId);
  const ticketPermissions = useSelector(selectTicketPermissions);
  const permissionToAssign = ticketPermissions.includes(
    TicketPermissionType.ReassignAny,
  );
  const { data } = api.endpoints.getRecipients.useQueryState(
    {
      ticketId,
    },
    {
      skip: !ticketId || !permissionToAssign,
    },
  );
  const allAssignedSignerIds: PilotId[] =
    data?.signers
      ?.filter((otherRecipient) => otherRecipient.userId !== null)
      .map((signer) => signer.userId as PilotId) ?? [];

  const {
    data: envelope,
    isLoading: isEnvelopeLoading,
    isError: isEnvelopeError,
  } = api.endpoints.getEnvelope.useQueryState(
    {
      ticketId,
    },
    {
      skip: !ticketId || !permissionToAssign,
    },
  );
  const noEnvelope = !envelope && !isEnvelopeLoading && !isEnvelopeError;
  const allowInternalAssignment =
    noEnvelope && !recipient.isExternal && permissionToAssign;
  const allowInternalReassignment =
    allowInternalAssignment && !!recipient.external?.email;

  const [newAssignedId, setNewAssignedId] = useState<PilotId>('');
  const [assignSigner] = api.endpoints.assignSigner.useMutation();
  const { reassignSignerModal, showReassignSignerModal } =
    useReassignSignerModal({
      handleAssign: async () => {
        try {
          await assignSigner({
            ticketId,
            recipientId: recipient.id,
            userId:
              typeof newAssignedId === 'string'
                ? parseInt(newAssignedId)
                : newAssignedId,
          }).unwrap();
        } catch (error) {
          if (error instanceof Error) {
            toast(error, {
              autoDismiss: false,
            });
          }
        }
      },
      handleHide: () => {
        setNewAssignedId('');
      },
    });

  const handleAssignment = (newAssignedId: PilotId) => {
    showReassignSignerModal();
    setNewAssignedId(newAssignedId);
  };

  return {
    allowInternalReassignment,
    allowInternalAssignment,
    handleAssignment,
    allAssignedSignerIds,
    reassignSignerModal,
  };
};
